/**
 * Why offline / Fleet Diagnose desk (Page170 residual / page137 / page68).
 * Operator copy is path-free. Quiet list + presence diagnose are BE-gated
 * (devices ACL + assert_device_view_access). Soft invent stayed deleted.
 */
import { useMemo, useState } from "react";
import { html, M, F, Ico, Card, KV, Alert, FADE, go } from "./ui.js";
import * as L from "./fleet-data.js";
import { quietDevicesOf, presenceDiagnoseView } from "./fleet-live.js";
import { path, useApi } from "./api.js";
import { boardHeadingOf, looksLikeCode, operatorSay } from "./operator-say.js";

/** Path-free operator copy for Diagnose desk failures. */
export function softFleetDiagnoseErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|not_authorized|denied|access/.test(low)) {
    return "Your account cannot see this unit. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|device_not_found/.test(low)) {
    return "That unit is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline/.test(low)) {
    return "Presence diagnose is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/fleet\/|\/devices\/|presence-diagnose|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Pure: format diagnose age seconds; invent ages forbidden. */
export function diagnoseAgeOf(n) {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) < 0) return "—";
  const v = Number(n);
  if (v < 60) return Math.round(v) + "s";
  if (v < 3600) return Math.floor(v / 60) + "m";
  return Math.floor(v / 3600) + "h";
}

/** Pure: quiet-list KPI strip. */
export function diagnoseKpisOf(quiet, totalLoaded) {
  const q = Array.isArray(quiet) ? quiet.length : 0;
  const t = Number(totalLoaded) || 0;
  return {
    quiet: q,
    loaded: t,
    answering: Math.max(0, t - q),
  };
}

/** Pure: filter quiet rows by needle. */
export function filterQuietOf(quiet, q) {
  const list = Array.isArray(quiet) ? quiet : [];
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return list;
  return list.filter((d) => {
    if (!d) return false;
    const hay = [d.label, d.id, d.zone].join(" ").toLowerCase();
    return hay.includes(needle);
  });
}

export function FleetDiagnoseDesk({ toast }) {
  const posture = useApi(path("get__fleet_diagnose_status"));
  const post = posture.data || {};
  /* Fail-closed while posture missing booleans. */
  const registerOk = post.register_allowed === true;
  const diagnoseOk = post.diagnose_allowed === true;
  const detail = post.detail || null;

  const devices = useApi(registerOk ? path("get__devices") : "");
  const items = (devices.data && (devices.data.items || devices.data.devices)) || [];
  const quiet = useMemo(() => quietDevicesOf(devices.data), [devices.data]);
  const [sel, setSel] = useState("");
  const [q, setQ] = useState("");
  const diagPath = (diagnoseOk && sel)
    ? path("get__devices_p_presence-diagnose", { pathParams: [sel] })
    : "";
  const diagLive = useApi(diagPath);
  const view = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const filtered = useMemo(() => filterQuietOf(quiet, q), [quiet, q]);
  const kpis = diagnoseKpisOf(quiet, items.length);
  const bucketMeta = view && view.bucket
    ? (L.BUCKETS.find((x) => x.id === view.bucket) || null)
    : null;
  const threshold = (view && view.thresholdS) || 120;
  const err = devices.error || diagLive.error;

  const reRead = () => {
    posture.reload && posture.reload();
    devices.reload && devices.reload();
    diagLive.reload && diagLive.reload();
    toast && toast("Why offline refreshed", "go");
  };

  if (!posture.loading && post.enabled !== true) {
    return html`<div className="fleet-diag-desk fleet-diag-desk--liquid"
      data-cmc-page="page137-diagnose" data-cmc-legacy="page68-diagnose"
      data-cmc-residual="page170-diagnose">
      <div className="fleet-diag-desk__rail">
        <div>
          <p className="eyebrow">Fleet · Why offline</p>
          <h2 className="fleet-diag-desk__title">Seat closed</h2>
          <p className="note">Your seat cannot open Why offline on this grant.</p>
        </div>
        <div className="btn-row fleet-diag-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Refresh</button>
        </div>
      </div>
      <${Card} title="Your seat" meta=${post.posture_label || "Seat closed"}>
        <p className="note">${post.posture_line || detail || "Seat closed"}</p>
      <//>
    </div>`;
  }

  return html`<div className="fleet-diag-desk fleet-diag-desk--liquid"
    data-cmc-page="page137-diagnose" data-cmc-legacy="page68-diagnose"
    data-cmc-residual="page170-diagnose">
    <div className="fleet-diag-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Why offline</p>
        <h2 className="fleet-diag-desk__title">Quiet register</h2>
        <p className="note">
          ${post.posture_line || "See which units went quiet, then run a live presence check on one."}
        </p>
      </div>
      <div className="btn-row fleet-diag-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead} disabled=${posture.loading}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/ops/devices")}>Ops devices</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/fleet/health")}>Fleet health</button>
      </div>
    </div>

    <${Alert} tone="info" title="Why offline"
      say="Quiet units come from your live device register. Root cause runs only when you select a unit you can see. Empty means empty."/>

    <${Card} title="Your seat" meta=${post.posture_label || (registerOk ? "Register open" : "Closed")}>
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="register" v=${registerOk ? "open" : "closed"}/>
        <${KV} k="presence check" v=${diagnoseOk ? "open" : "closed"}/>
        <${KV} k="taxonomy write" v=${"read only"}/>
      </div>
      ${detail ? html`<p className="note" style=${{ marginTop: 8 }}>${post.posture_line || detail}</p>` : null}
    <//>

    ${err ? html`<${Alert} tone="warn" title="Could not load diagnose"
      say=${softFleetDiagnoseErrorOf(err)}/>` : null}

    <div className="fleet-diag-desk__kpis" role="group" aria-label="Diagnose KPIs">
      <div className="fleet-diag-desk__kpi" data-on=${kpis.quiet ? "1" : "0"} data-tone="off">
        <span>Quiet</span><b>${devices.loading && !quiet.length ? "…" : kpis.quiet}</b>
      </div>
      <div className="fleet-diag-desk__kpi" data-on=${kpis.loaded ? "1" : "0"}>
        <span>Loaded</span><b>${devices.loading && !items.length ? "…" : kpis.loaded}</b>
      </div>
      <div className="fleet-diag-desk__kpi" data-on=${kpis.answering ? "1" : "0"}>
        <span>Answering</span><b>${devices.loading && !items.length ? "…" : kpis.answering}</b>
      </div>
      <div className="fleet-diag-desk__kpi" data-on=${sel ? "1" : "0"}>
        <span>Selected</span><b>${sel ? "1" : "0"}</b>
      </div>
    </div>

    <${Card} title="Quiet devices"
        meta=${!registerOk
          ? "register closed"
          : (devices.loading && !quiet.length ? "reading…" : (quiet.length ? (quiet.length + " quiet") : "none quiet"))}
        sub=${"Offline threshold context " + threshold + "s after a diagnose · filter on this page only."}>
      ${!registerOk ? html`
        <p className="note">Register closed for this seat — quiet list stays empty.</p>`
      : html`
      <div className="inp" style=${{ height: 38, marginBottom: 10 }}>
        <${Ico} d="search" w=${14}/>
        <input value=${q} onChange=${(e) => setQ(e.target.value)}
          placeholder="Filter by label, id, or zone"
          aria-label="Filter quiet devices"
          style=${{ fontSize: "var(--t-body)" }}/>
      </div>
      ${!filtered.length ? html`
        <p className="note">${devices.loading
          ? "Reading the device register…"
          : quiet.length
            ? "Nothing matches this filter."
            : "No quiet devices on this account — every visible unit answered, or none are loaded."}</p>`
      : html`
        <div className="rows">
          ${filtered.slice(0, 40).map((d) => html`
            <button key=${d.id} type="button" className="row"
              data-on=${sel === d.id ? "1" : "0"} data-state="off"
              onClick=${() => setSel(d.id)}>
              <span className="st"/>
              <span className="main"><b>${boardHeadingOf(d.label || d.id)}</b>
                <span>${d.zone && !looksLikeCode(d.zone) ? d.zone : "This site"}</span></span>
              <span className="trail">${d.ageS != null ? (diagnoseAgeOf(d.ageS) + " quiet") : "quiet"}</span>
            </button>`)}
        </div>
        ${filtered.length > 40 ? html`
          <p className="note">Showing 40 of ${filtered.length}. Narrow the filter.</p>` : null}`}
      `}
    <//>

    <${Card} title="Presence check"
        meta=${!diagnoseOk
          ? "Check closed"
          : (sel
            ? (view ? (view.online ? "Online" : "Quiet") : (diagLive.loading ? "Reading…" : "Ready"))
            : "Pick a quiet board")}>
      ${!diagnoseOk ? html`<p className="note">Presence check closed for this seat.</p>` : null}
      ${diagnoseOk && !sel ? html`<p className="note">Select a quiet device to run a presence check.</p>` : null}
      ${diagnoseOk && sel && diagLive.loading && !view ? html`<p className="note">Running presence check…</p>` : null}
      ${view ? html`<${F}>
        <${KV} k="Board" v=${boardHeadingOf(view.deviceId)}/>
        <${KV} k="State" v=${view.online ? "Online" : "Quiet"} tone=${view.online ? "hi" : "warn"}/>
        <${KV} k="Suspect" v=${operatorSay(view.suspect, "—") || "—"} tone=${view.online ? "" : "warn"}/>
        <${KV} k="Signal reading" v=${view.reason ? operatorSay(view.reason, "Signal path") : "—"}/>
        <${KV} k="Offline threshold" v=${threshold + " s"}/>
        ${view.nextAction ? html`
          <div className="bktdet__a" style=${{ marginTop: 10 }}>
            <${Ico} d="target" w=${13}/><span>${view.nextAction}</span>
          </div>` : null}
        ${bucketMeta ? html`
          <p className="note" style=${{ marginTop: 10 }}>${bucketMeta.say} · ${bucketMeta.act}</p>` : null}
        <div className="btn-row" style=${{ marginTop: 12 }}>
          <button type="button" className="btn btn--go btn--sm"
            onClick=${() => go("/fleet/device/" + encodeURIComponent(view.deviceId))}>
            Open estate card</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/ops/devices")}>
            Ops devices</button>
        </div>
      <//>` : null}
    <//>

    <div className="grid g2">
      <${Card} title="Last contact" meta=${"— = never · warn over " + threshold + " s"}>
        ${!view ? html`<p className="note">Ages appear after a presence check.</p>` : html`<${F}>
          <${KV} k="Heartbeat" v=${diagnoseAgeOf(view.ages.heartbeat)}
            tone=${view.ages.heartbeat != null && view.ages.heartbeat > threshold ? "warn" : ""}/>
          <${KV} k="Last signal" v=${diagnoseAgeOf(view.ages.signal)}
            tone=${view.ages.signal != null && view.ages.signal > threshold ? "warn" : ""}/>
          <${KV} k="Row updated" v=${diagnoseAgeOf(view.ages.updated)}
            tone=${view.ages.updated != null && view.ages.updated > threshold ? "warn" : ""}/>
          <${KV} k="Status frame" v=${diagnoseAgeOf(view.ages.status)}/>
        <//>`}
      <//>
      <${Card} title="Counters since boot" meta="from net health">
        ${!view ? html`<p className="note">Counters appear after a presence check.</p>` : html`<${F}>
          <${KV} k="Wi‑Fi reconnects" v=${view.net.wifi == null ? "—" : String(view.net.wifi)}/>
          <${KV} k="MQTT reconnects" v=${view.net.mqtt == null ? "—" : String(view.net.mqtt)}/>
          <${KV} k="MQTT fail streak" v=${view.net.failStreak == null ? "—" : String(view.net.failStreak)}
            tone=${view.net.failStreak != null && view.net.failStreak >= 3 ? "warn" : ""}/>
          <${KV} k="Longest MQTT gap" v=${view.net.longestGapMs == null ? "—" : (Math.round(view.net.longestGapMs / 1000) + " s")}/>
        <//>`}
      <//>
    </div>

    <${Card} title="Attribution taxonomy · reference"
        meta="labels only · not a write control">
      <p className="note" style=${{ margin: "0 0 10px" }}>
        Buckets are labels for live suspects. Highlight follows the diagnose result —
        click-to-toast invent stays deleted.
      </p>
      <div className="buckets">
        ${L.BUCKETS.map((x) => html`
          <div key=${x.id} className="bkt" data-on=${view && view.bucket === x.id ? "1" : "0"}
            role="status" aria-label=${x.t}>
            <span>${x.t}</span>
          </div>`)}
      </div>
      ${view && view.bucket ? html`
        <${M.div} className="bktdet" initial=${{ opacity: 0, y: 8 }}
            animate=${{ opacity: 1, y: 0 }} transition=${FADE}>
          <p><b>${view.suspect}</b> · ${view.nextAction || "No next action on this diagnose."}</p>
        <//>` : null}
    <//>

    <${Card} title="How online is decided">
      ${view && view.reason ? html`
        <div className="reason" data-on="1">
          <b>${operatorSay(view.reason, String(view.reason).replace(/_/g, " "))}</b>
          <span>Reading from the live presence check.</span>
        </div>` : html`<p className="note">Select a device to see the decision reason.</p>`}
      ${L.REASONS.map(([k, s]) => html`
        <div key=${k} className="reason"
          data-on=${view && view.reason && view.reason.replace(/_/g, " ").toLowerCase()
            .includes(String(k).toLowerCase().slice(0, 8)) ? "1" : "0"}>
          <b>${k}</b><span>${s}</span>
        </div>`)}
    <//>
  </div>`;
}
